  "use client"

  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
  import { Badge } from "../ui/badge"
  import { Textarea } from "../ui/textarea"
  import { Button } from "../ui/button"
  import { CheckCircle, XCircle, Clock, Calendar, Timer } from "lucide-react"
  import { format, getDay } from "date-fns"
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from "../ui/table"

  interface LeaveReviewModalEmpProps {
    isOpen: boolean
    onClose: () => void
    leaveRequest: any // Ideally define a proper type
    isViewOnly?: boolean
  }

  export function LeaveReviewModalEmp({
    isOpen,
    onClose,
    leaveRequest,
    isViewOnly = false
  }: LeaveReviewModalEmpProps) {

    // Calculate total leave days from leaveDayDetails
    const totalDays = leaveRequest?.leaveDayDetails?.reduce((sum: number, d: any) => {
      if (!d.dayType) return sum;
      const dayType = d.dayType.toLowerCase();
      if (dayType === "full day") return sum + 1;
      if (dayType === "first-half" || dayType === "second-half") return sum + 0.5;
      return sum;
    }, 0) || leaveRequest?.days || 0

    // Helper for status badge
    const getApprovalBadge = (status: string) => {
      switch (status.toLowerCase()) {
        case "approved":
          return <Badge className="bg-green-100 text-green-700 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</Badge>
        case "pending":
          return <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</Badge>
        case "rejected":
          return <Badge className="bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="h-3 w-3" /> Rejected</Badge>
        case "pending_manager_approval":
        case "pending manager approval":
          return <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1"><Clock className="h-3 w-3" /> Pending Manager Approval</Badge>
        case "pending_hr_approval":
        case "pending hr approval":
          return (
            <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Pending HR Approval
            </Badge>
          )
        default:
          return <Badge variant="outline">{status}</Badge>
      }
    }

    // Helper to format date
    const formatDate = (dateString: string | null | undefined) => {
      if (!dateString) return "--";
      try {
        const date = new Date(dateString);
        return format(date, "dd-MM-yyyy");
      } catch {
        return "--";
      }
    };

    // Helper to format date with time
    const formatDateTime = (dateString: string | null | undefined) => {
      if (!dateString) return "--";
      try {
        const date = new Date(dateString);
        return format(date, "dd-MM-yyyy HH:mm");
      } catch {
        return "--";
      }
    };

    // Helper to get day name
    const getDayName = (dateString: string | null | undefined) => {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return format(date, "EEEE");
      } catch {
        return "";
      }
    };

    // Helper to get day type color
    const getDayTypeColor = (dayType: string) => {
      const lower = dayType?.toLowerCase() || "";
      if (lower === "full day") return "bg-blue-500";
      if (lower === "first-half") return "bg-purple-500";
      if (lower === "second-half") return "bg-purple-500";
      return "bg-blue-500";
    };

    // Helper to format day type badge
    const formatDayTypeBadge = (dayType: string) => {
      if (!dayType) return "Full Day";
      const lower = dayType.toLowerCase();
      if (lower === "first-half") return "First-Half";
      if (lower === "second-half") return "Second-Half";
      return "Full Day";
    };

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-7xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          <DialogHeader className="border-b border-gray-200 pb-3 px-4 pt-4 flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-bold text-gray-900">
                Leave Details
              </DialogTitle>
              <Badge className="text-orange-600 border-orange-200 bg-orange-50 text-xs font-medium">
                {leaveRequest?.type || "Leave"}
              </Badge>
            </div>
            {/* Applied On in header */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Applied On</span>
                <span className="text-lg font-semibold text-gray-900">
                  {leaveRequest?.appliedDate ? leaveRequest?.appliedDate : "--"}
                </span>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 px-4">
            {/* Leave Day Details at top with individual day cards */}
            {leaveRequest?.leaveDayDetails && leaveRequest.leaveDayDetails.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 className="font-semibold text-gray-900 text-base">Leave Day Details</h3>
                </div>
                <div className="flex flex-wrap gap-16">
                  {leaveRequest.leaveDayDetails.map((dayDetail: any, index: number) => {
                    const dayTypeColor = getDayTypeColor(dayDetail.dayType);
                    const dayTypeBadge = formatDayTypeBadge(dayDetail.dayType);
                    const dayName = getDayName(dayDetail.leaveDate);
                    
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${dayTypeColor}`}></div>
                        <div className="flex flex-col">
                          <span className="text-base font-semibold text-gray-900">
                            {dayDetail.leaveDate ? formatDate(dayDetail.leaveDate) : "--"}
                          </span>
                          <span className="text-xs text-gray-500">{dayName}</span>
                        </div>
                      <Badge className={`${
                        dayTypeBadge === "Full Day" 
                          ? "bg-blue-50 text-blue-700 border-blue-200" 
                          : "bg-purple-50 text-purple-600 border-purple-200"
                      } font-medium text-xs`}>
                          {dayTypeBadge}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 className="font-semibold text-gray-900 text-base">Leave Day Details</h3>
                </div>
                <p className="text-sm text-gray-500 mt-1">No leave day details available</p>
              </div>
            )}

            {/* Reason for Leave and Duration side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Reason for Leave */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <h3 className="font-semibold text-gray-900 text-base">Reason for Leave</h3>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {leaveRequest?.reason || <span className="italic text-gray-400">No reason provided</span>}
                </p>
              </div>

              {/* Duration */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Timer className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">DURATION</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {totalDays} {totalDays === 1 ? "Day" : "Days"}
                </p>
              </div>
            </div>

            {/* If leave is cancelled, show info banner */}
            {leaveRequest?.isCancelled && (
              <div className="border-l-4 border-yellow-500 bg-yellow-50 p-3 rounded-r-lg">
                <div className="flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-yellow-600" />
                  <p className="font-medium text-gray-900 text-sm">
                    User has already cancelled their leave
                  </p>
                </div>
              </div>
            )}

            {/* Approval Status */}
            {!leaveRequest?.isCancelled && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 text-lg">Approval Status</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Manager Approval */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 text-base">Manager Approval</h4>
                      {getApprovalBadge(leaveRequest?.managerResponse || "pending_manager_approval")}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start">
                        <span className="font-medium text-gray-500 w-24">Reviewer</span>
                        <span className="text-gray-900 flex-1">
                          {leaveRequest?.managerName || "--"}
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="font-medium text-gray-500 w-24">Reviewed On</span>
                        <span className="text-gray-900 flex-1">
                          {formatDateTime(leaveRequest?.managerResponseOn)}
                        </span>
                      </div>
                      {leaveRequest?.managerComment &&
                        leaveRequest.managerComment.trim() !== "" &&
                        leaveRequest.managerResponse?.toLowerCase() !== "pending" && (
                          <div className="pt-2 border-t border-gray-200">
                            <span className="font-medium text-gray-500 block mb-1">Comment</span>
                            <p className="text-gray-700 break-all whitespace-pre-wrap text-sm">
                              {leaveRequest.managerComment}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* HR Approval */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 text-base">HR Approval</h4>
                      {getApprovalBadge(leaveRequest?.hrResponse || "Pending")}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start">
                        <span className="font-medium text-gray-500 w-24">Reviewer</span>
                        <span className="text-gray-900 flex-1">
                          {leaveRequest?.hrName || "--"}
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="font-medium text-gray-500 w-24">Reviewed On</span>
                        <span className="text-gray-900 flex-1">
                          {formatDateTime(leaveRequest?.hrResponseOn)}
                        </span>
                      </div>
                      {leaveRequest?.hrComment &&
                        leaveRequest.hrComment.trim() !== "" &&
                        leaveRequest.hrResponse?.toLowerCase() !== "pending" && (
                          <div className="pt-2 border-t border-gray-200">
                            <span className="font-medium text-gray-500 block mb-1">Comment</span>
                            <p className="text-gray-700 break-all whitespace-pre-wrap text-sm">
                              {leaveRequest.hrComment}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }
